import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, Loader2, Lock, Sparkles, Users, Zap, Bot, ShieldCheck, Mail } from "lucide-react";
import Logo from "@/components/Logo";
import ContactSalesDialog from "@/components/ContactSalesDialog";

/**
 * BidForge Pricing (Feb 2026 catalog).
 *
 * Backend `/api/billing/plan-catalog` is the SINGLE SOURCE OF TRUTH.  This
 * page renders whatever the backend returns so a config change never requires
 * a frontend deploy.  Monthly/Annual toggle is UI-only — the annual price is
 * pulled from the catalog and reflects the workspace-wide 20% discount.
 */

const TONES = {
  starter:      { eyebrow: "text-sky-600",     ring: "ring-sky-200",     grad: "from-sky-50 to-white",       chip: "bg-sky-100 text-sky-700",         Icon: Users },
  professional: { eyebrow: "text-violet-600",  ring: "ring-violet-200",  grad: "from-violet-50 to-white",    chip: "bg-violet-100 text-violet-700",   Icon: Zap },
  business:     { eyebrow: "text-fuchsia-600", ring: "ring-fuchsia-200", grad: "from-fuchsia-50 to-white",   chip: "bg-fuchsia-100 text-fuchsia-700", Icon: Bot },
  enterprise:   { eyebrow: "text-amber-600",   ring: "ring-amber-200",   grad: "from-amber-50 to-white",     chip: "bg-amber-100 text-amber-700",     Icon: ShieldCheck },
};

// Feature checklist per tier — pulled from PLAN_CATALOG + hand-authored copy.
const FEATURES = {
  starter: [
    "AI opportunity search (SAM.gov)",
    "Match scoring + saved searches",
    "Full 14-section proposal drafts",
    "Bid/No-Bid executive briefs",
    "Company profile + past performance",
    "Includes 100 AI credits / mo",
  ],
  professional: [
    "Everything in Starter",
    "40 proposals/mo (vs. 10)",
    "Team seats: 3 included, up to 5",
    "Compliance sweep on every estimate",
    "Amendment monitoring + alerts",
    "House-style skills (proposal voice)",
    "Includes 500 AI credits / mo",
  ],
  business: [
    "Everything in Professional",
    "Unlimited proposals / month",
    "Team seats: 5 included, up to 10",
    "Autonomous Agent (up to 10/day)",
    "Pipeline forecasting",
    "API access + Zapier",
    "Includes 2,000 AI credits / mo",
  ],
  enterprise: [
    "Everything in Business",
    "Unlimited seats + SSO",
    "Custom AI deployments",
    "Dedicated account manager",
    "Priority support · 4-hour SLA",
    "Onboarding + capture playbooks",
  ],
};

const TAGLINE = { starter: "You bid", professional: "Your team bids", business: "BidForge bids", enterprise: "Your division bids" };

function fmt(cents) {
  if (cents == null) return "Custom";
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/plan-catalog").then(r => setCatalog(r.data)).catch(() => {
      toast.error("Pricing catalog unavailable");
    });
  }, []);

  const reason = params.get("reason");
  const upgradeFrom = params.get("from");
  const required = params.get("required");
  const reasonCopy = {
    trial_expired: "Your 7-day trial has ended. Choose a plan to keep your workspace active.",
    paid_plan_required: `That feature isn't included in your current plan. Upgrade to ${required ? required.charAt(0).toUpperCase() + required.slice(1) : "Professional"} to unlock it.`,
    plan_upgrade_required: `That feature requires the ${required ? required.charAt(0).toUpperCase() + required.slice(1) : "Business"} plan.`,
    subscription_inactive: "Your subscription is inactive. Reactivate below to continue.",
    monthly_cap_reached: "You've hit your monthly proposal cap. Upgrade to increase your monthly ceiling and lower your overage rate.",
  };

  const subscribe = async (planKey) => {
    // Enterprise CTA opens the contact-sales dialog for both anonymous
    // visitors and signed-in users — this is a lead-capture path, not a
    // self-serve checkout.
    if (planKey === "enterprise") { setSalesOpen(true); return; }
    if (!user) { nav("/signup"); return; }
    setBusy(planKey);
    try {
      const r = await api.post("/payments/checkout", { plan: planKey, origin_url: window.location.origin, billing_cycle: annual ? "annual" : "monthly" });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const plans = useMemo(() => {
    if (!catalog) return [];
    return ["starter", "professional", "business", "enterprise"].map((key) => ({ key, ...catalog.plans[key] }));
  }, [catalog]);

  const discountPct = catalog?.annual_discount_pct || 20;

  return (
    <div className="bf-light theme-light min-h-screen bg-white text-slate-900 relative overflow-hidden" data-testid="pricing-page">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl opacity-50" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.20), transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none absolute top-96 -left-40 w-[500px] h-[500px] rounded-full blur-3xl opacity-40" style={{ background: "radial-gradient(circle, rgba(236,72,153,0.15), transparent 70%)" }} />

      <div className="relative max-w-[1400px] mx-auto px-6 py-10">
        <button
          type="button"
          onClick={() => { if (window.history.length > 1) nav(-1); else nav(user ? "/dashboard" : "/"); }}
          className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-violet-600 flex items-center gap-2 mb-10"
          data-testid="back-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" />BACK
        </button>

        <div className="flex items-center gap-2 mb-8">
          <Logo className="w-9 h-9" />
          <div className="font-display font-black text-lg tracking-tight">BIDFORGE</div>
        </div>

        <div className="max-w-3xl mb-8">
          <div className="bf-eyebrow inline-block text-fuchsia-600 mb-6">PRICING</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tight mb-4 text-slate-900">
            Priced for the department it <span className="bf-grad-text">replaces</span>.
          </h1>
          <p className="text-slate-600 text-lg">Every tier includes AI opportunity discovery. Cancel any time.</p>
        </div>

        {reason && reasonCopy[reason] && (
          <div className="mb-10 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 flex items-start gap-3" data-testid="paywall-reason">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><Lock className="w-5 h-5" /></div>
            <div>
              <div className="text-xs font-mono font-bold tracking-widest text-amber-700 mb-1">PAYWALL</div>
              <div className="text-slate-700">{reasonCopy[reason]}</div>
            </div>
          </div>
        )}

        {/* Monthly / Annual toggle */}
        <div className="mb-10 flex items-center gap-4" data-testid="billing-toggle">
          <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            <button
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-xs font-mono font-bold tracking-widest transition-all ${!annual ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="toggle-monthly"
            >MONTHLY</button>
            <button
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-xs font-mono font-bold tracking-widest transition-all ${annual ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="toggle-annual"
            >ANNUAL</button>
          </div>
          <div className="inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-mono font-bold tracking-widest px-3 py-1.5">
            SAVE {discountPct}% ON ANNUAL
          </div>
        </div>

        {!catalog ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[0,1,2,3].map(i => <div key={i} className="rounded-2xl bg-slate-100 h-[520px] animate-pulse" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((p) => {
              const t = TONES[p.key];
              const Icon = t.Icon;
              const isCurrent = user?.plan === p.key;
              const isRecommended = upgradeFrom && ((upgradeFrom === "starter" && p.key === "professional") || (upgradeFrom === "professional" && p.key === "business") || (upgradeFrom === "business" && p.key === "enterprise"));
              const highlight = p.key === "professional" || isRecommended;
              const monthlyCents = p.monthly_cents;
              const annualCents = p.annual_cents;
              const perMonthAnnual = annualCents != null ? Math.round(annualCents / 12) : null;
              const displayCents = annual ? perMonthAnnual : monthlyCents;
              const totalAnnual = annualCents;

              return (
                <div
                  key={p.key}
                  className={`rounded-2xl p-8 flex flex-col bg-white border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                    highlight
                      ? "border-violet-300 shadow-[0_20px_60px_-15px_rgba(139,92,246,0.35)] ring-2 ring-violet-200/40"
                      : "border-slate-200 shadow-sm"
                  }`}
                  data-testid={`tier-${p.key}`}
                >
                  {/* Badge */}
                  {isRecommended
                    ? <div className="inline-flex self-start bf-pill text-[10px] px-3 py-1 mb-4 items-center gap-1 bg-emerald-500 text-white" data-testid={`recommended-${p.key}`}><Sparkles className="w-3 h-3" />RECOMMENDED</div>
                    : p.key === "professional"
                      ? <div className="inline-flex self-start bf-pill text-[10px] px-3 py-1 mb-4 items-center gap-1"><Sparkles className="w-3 h-3" />MOST POPULAR</div>
                      : <div className={`text-[10px] font-mono font-bold tracking-widest ${t.eyebrow} mb-4`}>{(TAGLINE[p.key] || p.label).toUpperCase()}</div>}

                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${t.chip}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="font-display font-black text-2xl text-slate-900">{p.label}</div>
                  </div>

                  <div className="text-xs text-slate-500 mb-4 italic">{TAGLINE[p.key]}</div>

                  <div className="mb-6">
                    {displayCents == null
                      ? <div className="font-display font-black text-4xl text-slate-900">Custom</div>
                      : (
                        <>
                          <div className="flex items-baseline">
                            <span className="font-display font-black text-5xl text-slate-900" data-testid={`price-${p.key}`}>{fmt(displayCents)}</span>
                            <span className="text-slate-500 ml-2 text-sm">/mo</span>
                          </div>
                          {annual && totalAnnual != null && (
                            <div className="text-xs text-emerald-600 mt-1 font-mono" data-testid={`annual-total-${p.key}`}>
                              {fmt(totalAnnual)} billed yearly
                            </div>
                          )}
                          {!annual && monthlyCents != null && annualCents != null && (
                            <div className="text-xs text-slate-400 mt-1 font-mono">
                              {fmt(annualCents)} yearly with annual
                            </div>
                          )}
                        </>
                      )}
                  </div>

                  {/* Cap chip */}
                  <div className="mb-3 space-y-1.5">
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold tracking-widest text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1 self-start">
                      {p.proposal_cap_monthly == null ? "UNLIMITED PROPOSALS" : `${p.proposal_cap_monthly} PROPOSALS / MO`}
                    </div>
                    {p.proposal_cap_monthly != null && p.overage_cents_per_proposal && (
                      <div className="text-[11px] text-slate-500 font-mono">
                        +${(p.overage_cents_per_proposal / 100).toFixed(0)}/proposal over cap
                      </div>
                    )}
                    <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold tracking-widest text-violet-700 bg-violet-50 border border-violet-100 rounded-full px-2.5 py-1 self-start">
                      {p.seats_included} SEAT{p.seats_included === 1 ? "" : "S"}{p.seats_max ? ` · UP TO ${p.seats_max}` : " · UNLIMITED"}
                    </div>
                  </div>

                  <ul className="space-y-2.5 mb-8 mt-4 flex-1">
                    {(FEATURES[p.key] || []).map((f) => (
                      <li key={f} className="text-sm text-slate-700 flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{f}
                      </li>
                    ))}
                  </ul>

                  {isCurrent
                    ? <div className="text-center py-3 rounded-full border-2 border-emerald-400 text-emerald-600 font-display font-bold text-xs tracking-widest uppercase" data-testid={`current-${p.key}`}>CURRENT PLAN</div>
                    : <button
                        onClick={() => subscribe(p.key)}
                        disabled={busy === p.key}
                        className={`text-center py-3 rounded-full font-display font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${highlight ? "bf-pill" : "bf-pill-ghost hover:!border-violet-400 hover:!text-violet-600"}`}
                        data-testid={`subscribe-${p.key}`}
                      >
                        {busy === p.key
                          ? <><Loader2 className="w-4 h-4 animate-spin" />REDIRECTING</>
                          : p.key === "enterprise"
                            ? <><Mail className="w-4 h-4" />Contact sales</>
                            : (isRecommended ? "Upgrade now" : "Subscribe")}
                      </button>}
                </div>
              );
            })}
          </div>
        )}

        {/* Comparison bar */}
        {catalog && (
          <div className="mt-16 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="comparison-strip">
            <div className="text-xs font-mono font-bold tracking-widest text-slate-500 mb-6">HOW BIDFORGE COMPARES</div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="font-display font-black text-3xl text-slate-900">1 platform</div>
                <div className="text-sm text-slate-600 mt-1">vs. SAM.gov + GovWin + Deltek + Word + Excel + a proposal shop</div>
              </div>
              <div>
                <div className="font-display font-black text-3xl text-slate-900">3 hours</div>
                <div className="text-sm text-slate-600 mt-1">to draft a 14-section compliant proposal (vs. 20-40 hours)</div>
              </div>
              <div>
                <div className="font-display font-black text-3xl text-slate-900">$99/mo</div>
                <div className="text-sm text-slate-600 mt-1">to start — vs. $2,000+/mo for GovWin IQ + Deltek Costpoint</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ContactSalesDialog open={salesOpen} onClose={() => setSalesOpen(false)} context="Requesting Enterprise plan pricing and demo." />
    </div>
  );
}
